import { OrbitControls, useContextBridge, useTexture } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { useAppSelector } from 'app/hooks';
import { ContextMenuContext } from 'components/common/context-menu-manager';
import {
  ConfigurationType,
  GridObjectType,
  GridSpaceType,
  LevelType,
  LineSetPointSelectionType
} from 'data/types';
import { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { ReactReduxContext } from 'react-redux';
import {
  BufferAttribute,
  Color,
  Path,
  RepeatWrapping,
  Shape,
  ShapeGeometry
} from 'three';
import {
  calculateLevelYOffsets,
  getBasementSpaceBounds,
  getItemsCenterOffset3D,
  getLevelIndexForSpace,
  getStructureTargetY,
  SpaceBounds
} from 'utils/3d';
import { FOOTPRINT_WALL_OPACITY } from 'utils/3d-footprint-walls';
import { resolveLinesetColor } from 'utils/3d-lineset-color';
import {
  buildConcealedDuctLayoutsForConfiguration,
  ZoneConcealedDuctLayout
} from 'utils/concealed-duct-geometry';
import { getConcealedDuctPlacementLevelIndex } from 'utils/concealed-duct-placement';
import { applyEquipmentDragOverrideToConfiguration } from 'utils/equipment-drag-display';
import {
  getEffectiveFootprintPolygons,
  hasFootprintPolygons
} from 'utils/footprint-polygons';
import { getGridSpacesForLevel } from 'utils/grid';
import { isLinesetDragActive } from 'utils/lineset-drag-state';
import { applySpaceOpacity, DEFAULT_SPACE_OPACITY } from 'utils/space-opacity';
import grassTexture from './assets/grass.jpg';
import EditableDuctRuns3D from './editable-duct-runs-3d';
import EditableLineSet3D from './editable-lineset-3d';
import Equipment3D from './equipment-3d';
import { EquipmentDragOverride } from './equipment-gizmo-3d';
import FootprintWalls3D from './footprint-walls-3d';
import LineSet3D from './lineset-3d';
import Space3D from './space-3d';

type Grid3DSceneProps = Readonly<{
  spaces: GridSpaceType[];
  objects: GridObjectType[];
  structure: LevelType[];
  activeLevelIndex: number;
  configurations?: ConfigurationType[];
  activeConfigIndex?: number;
  inactiveLevelOpacity?: number;
  configurationId?: string;
  concealedDuctLayouts?: ZoneConcealedDuctLayout[];
  readOnly?: boolean;
  spaceOpacity?: number;
  colorLinesetsByCondenser?: boolean;
}>;

type GroundWithCutoutsProps = Readonly<{
  basementBounds: SpaceBounds[];
}>;

function GroundWithCutouts({ basementBounds }: GroundWithCutoutsProps) {
  const texture = useTexture(grassTexture);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(10, 10);

  const geometry = useMemo(() => {
    const groundSize = 50;
    const halfSize = groundSize / 2;

    const shape = new Shape();
    shape.moveTo(-halfSize, -halfSize);
    shape.lineTo(halfSize, -halfSize);
    shape.lineTo(halfSize, halfSize);
    shape.lineTo(-halfSize, halfSize);
    shape.lineTo(-halfSize, -halfSize);

    for (const bounds of basementBounds) {
      const hole = new Path();
      hole.moveTo(bounds.minX, -bounds.minZ);
      hole.lineTo(bounds.maxX, -bounds.minZ);
      hole.lineTo(bounds.maxX, -bounds.maxZ);
      hole.lineTo(bounds.minX, -bounds.maxZ);
      hole.lineTo(bounds.minX, -bounds.minZ);
      shape.holes.push(hole);
    }

    const geo = new ShapeGeometry(shape);

    const positionAttr = geo.getAttribute('position');
    const uvs = new Float32Array(positionAttr.count * 2);

    for (let i = 0; i < positionAttr.count; i += 1) {
      const x = positionAttr.getX(i);
      const y = positionAttr.getY(i);
      uvs[i * 2] = (x + halfSize) / groundSize;
      uvs[i * 2 + 1] = (y + halfSize) / groundSize;
    }

    geo.setAttribute('uv', new BufferAttribute(uvs, 2));

    return geo;
  }, [basementBounds]);

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]}>
      <primitive object={geometry} attach='geometry' />
      <meshStandardMaterial map={texture} />
    </mesh>
  );
}

export default function Grid3DScene({
  spaces,
  objects,
  structure,
  activeLevelIndex,
  configurations = [],
  activeConfigIndex = 0,
  inactiveLevelOpacity = 0.3,
  configurationId,
  concealedDuctLayouts = [],
  readOnly = false,
  spaceOpacity = DEFAULT_SPACE_OPACITY,
  colorLinesetsByCondenser = false
}: Grid3DSceneProps) {
  const activeConfiguration = configurations[activeConfigIndex];
  const { zoneDuctModes } = useAppSelector((state) => state.ui);
  const ContextBridge = useContextBridge(ReactReduxContext, ContextMenuContext);
  const [selectedPoint, setSelectedPoint] =
    useState<LineSetPointSelectionType | null>(null);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(
    null
  );
  const [selectedDuctPointKey, setSelectedDuctPointKey] = useState<
    string | null
  >(null);
  const [equipmentDragOverride, setEquipmentDragOverride] =
    useState<EquipmentDragOverride | null>(null);
  const pointerDownPositionRef = useRef<{ x: number; y: number } | null>(null);

  const handleSelectEquipment = useCallback((objectId: string) => {
    setSelectedEquipmentId(objectId);
    setSelectedPoint(null);
    setSelectedDuctPointKey(null);
  }, []);

  const handleSelectDuctPoint = useCallback((pointKey: string | null) => {
    setSelectedDuctPointKey(pointKey);
    if (pointKey) {
      setSelectedPoint(null);
      setSelectedEquipmentId(null);
    }
  }, []);

  const handleEquipmentDragOverrideChange = useCallback(
    (override: EquipmentDragOverride | null) => {
      setEquipmentDragOverride(override);
    },
    []
  );

  const levelYOffsets = useMemo(
    () => calculateLevelYOffsets(structure),
    [structure]
  );

  const { offsetX, offsetZ } = useMemo(() => {
    const boundsItems = spaces.length > 0 ? spaces : objects;
    const footprintItems = structure.flatMap((level) =>
      getEffectiveFootprintPolygons(level).map((footprint) => ({
        position: { x: 0, y: 0, width: 0, height: 0 },
        vertices: footprint
      }))
    );
    return getItemsCenterOffset3D([...boundsItems, ...footprintItems]);
  }, [spaces, objects, structure]);

  const targetY = useMemo(() => getStructureTargetY(structure), [structure]);

  const basementBounds = useMemo(
    () => getBasementSpaceBounds(spaces, structure, offsetX, offsetZ),
    [spaces, structure, offsetX, offsetZ]
  );

  const getYOffsetForLevel = (levelIndex: number): number => {
    return levelYOffsets[levelIndex]?.yOffset ?? 0;
  };

  const getLevelTypeForIndex = (levelIndex: number): string => {
    return structure[levelIndex]?.type ?? 'level';
  };

  const handlePointerMissed = useCallback((event: MouseEvent) => {
    if (isLinesetDragActive()) return;
    const start = pointerDownPositionRef.current;
    if (start) {
      const distance = Math.hypot(
        event.clientX - start.x,
        event.clientY - start.y
      );
      if (distance > 6) return;
    }
    setSelectedPoint(null);
    setSelectedEquipmentId(null);
    setSelectedDuctPointKey(null);
  }, []);

  const displayObjects = useMemo(() => {
    if (!equipmentDragOverride) return objects;
    return objects.map((object) =>
      object.id === equipmentDragOverride.objectId
        ? {
            ...object,
            position: {
              ...object.position,
              x: equipmentDragOverride.x,
              y: equipmentDragOverride.y
            }
          }
        : object
    );
  }, [objects, equipmentDragOverride]);

  const displayConfiguration = useMemo(
    () =>
      applyEquipmentDragOverrideToConfiguration(
        activeConfiguration,
        equipmentDragOverride,
        objects
      ),
    [activeConfiguration, equipmentDragOverride, objects]
  );

  const displayConcealedDuctLayouts = useMemo(() => {
    if (!equipmentDragOverride) return concealedDuctLayouts;
    const draggedObject = objects.find(
      (o) => o.id === equipmentDragOverride.objectId
    );
    const isConcealedAirHandler =
      draggedObject?.type === 'air-handler' &&
      (draggedObject.airHandlerTypeCode === 'concealed' ||
        draggedObject.airHandlerTypeCode === 'concealed_high_static');
    if (!isConcealedAirHandler) return concealedDuctLayouts;

    const layoutMap = buildConcealedDuctLayoutsForConfiguration(
      displayConfiguration,
      structure,
      (levelIndex) => getGridSpacesForLevel(structure, levelIndex),
      zoneDuctModes
    );
    return Array.from(layoutMap.values());
  }, [
    concealedDuctLayouts,
    equipmentDragOverride,
    objects,
    displayConfiguration,
    structure,
    zoneDuctModes
  ]);

  return (
    <div
      className='h-full w-full'
      onPointerDownCapture={(event) => {
        pointerDownPositionRef.current = {
          x: event.clientX,
          y: event.clientY
        };
      }}
    >
      <Canvas
        className='h-full w-full'
        camera={{ position: [20, 12 + targetY, 20], fov: 60 }}
        gl={{ antialias: true }}
        scene={{ background: new Color('#87CEEB') }}
        shadows
        onPointerMissed={handlePointerMissed}
      >
        <ContextBridge>
          <Suspense fallback={null}>
            <ambientLight intensity={0.4} />
            <directionalLight
              position={[15, 20, 10]}
              intensity={1}
              castShadow
            />
            <directionalLight position={[-10, 15, -5]} intensity={0.6} />
            <pointLight position={[0, 8, 0]} intensity={0.4} />
            <group>
              <GroundWithCutouts basementBounds={basementBounds} />
            </group>
            <group position={[offsetX, 0, offsetZ]}>
              {structure.map((level, levelIndex) => {
                if (!hasFootprintPolygons(level)) return null;
                const isActive = levelIndex === activeLevelIndex;
                const levelOpacity = isActive ? 1 : inactiveLevelOpacity;
                if (levelOpacity === 0) return null;
                return (
                  <FootprintWalls3D
                    key={`footprint-walls-${level.uniqueId}`}
                    level={level}
                    yOffset={getYOffsetForLevel(levelIndex)}
                    opacity={FOOTPRINT_WALL_OPACITY * levelOpacity}
                  />
                );
              })}
              {spaces.map((space) => {
                const levelIndex = getLevelIndexForSpace(space.id, structure);
                const yOffset = getYOffsetForLevel(levelIndex);
                const levelType = getLevelTypeForIndex(levelIndex);
                const isActive = levelIndex === activeLevelIndex;
                const opacity = applySpaceOpacity(
                  isActive ? 1 : inactiveLevelOpacity,
                  spaceOpacity
                );

                if (opacity === 0) return null;

                return (
                  <Space3D
                    key={space.id}
                    space={space}
                    allSpaces={spaces}
                    yOffset={yOffset}
                    opacity={opacity}
                    showLabel={isActive}
                    levelType={levelType}
                  />
                );
              })}
              {displayObjects.map((object) => {
                const getLevelIndexForObject = (): number => {
                  if (object.levelUniqueId) {
                    const idx = structure.findIndex(
                      (l) => l.uniqueId === object.levelUniqueId
                    );
                    if (idx >= 0) return idx;
                  }
                  if (object.type === 'air-handler') {
                    return getLevelIndexForSpace(object.itemId, structure);
                  }
                  if (object.type === 'electrical-panel') {
                    for (
                      let levelIdx = 0;
                      levelIdx < structure.length;
                      levelIdx += 1
                    ) {
                      const level = structure[levelIdx];
                      if (
                        level.electricalPanels.some(
                          (panel) => panel.uniqueId === object.id
                        )
                      ) {
                        return levelIdx;
                      }
                    }
                  }
                  if (object.type === 'condenser') {
                    const condenser = configurations
                      .flatMap((config) => config.condensers)
                      .find((c) => c.uniqueId === object.id);
                    if (condenser?.levelUniqueId) {
                      const idx = structure.findIndex(
                        (l) => l.uniqueId === condenser.levelUniqueId
                      );
                      if (idx >= 0) return idx;
                    }
                  }
                  return 0;
                };

                const levelIndex = getLevelIndexForObject();
                const yOffset = getYOffsetForLevel(levelIndex);
                const placementLevelIndex =
                  object.type === 'air-handler'
                    ? getConcealedDuctPlacementLevelIndex(
                        levelIndex,
                        object.concealedLocation
                      )
                    : levelIndex;
                const isActive =
                  levelIndex === activeLevelIndex ||
                  placementLevelIndex === activeLevelIndex;
                const opacity = isActive ? 1 : inactiveLevelOpacity;

                if (opacity === 0) return null;

                return (
                  <Equipment3D
                    key={object.id}
                    object={object}
                    spaces={spaces}
                    structure={structure}
                    levelIndex={levelIndex}
                    yOffset={yOffset}
                    opacity={opacity}
                    configurationId={configurationId}
                    readOnly={readOnly}
                    selected={selectedEquipmentId === object.id}
                    onSelect={handleSelectEquipment}
                    onDragOverrideChange={handleEquipmentDragOverrideChange}
                  />
                );
              })}
              {displayConfiguration?.lineSets?.map((lineset) => {
                const condenserLevelIndex =
                  structure.findIndex(
                    (l) => l.uniqueId === lineset.condenserLevelUniqueId
                  ) || 0;
                const distinctPointLevels = new Set(
                  lineset.points
                    .map((point) => point.levelUniqueId)
                    .filter(Boolean)
                );
                const isMultiLevel =
                  lineset.isCrossFloor === true || distinctPointLevels.size > 1;
                const yOffset = isMultiLevel
                  ? 0
                  : getYOffsetForLevel(condenserLevelIndex);
                const airHandlerLevelIndex = structure.findIndex(
                  (l) => l.uniqueId === lineset.airHandlerLevelUniqueId
                );
                const isActive =
                  isMultiLevel ||
                  condenserLevelIndex === activeLevelIndex ||
                  airHandlerLevelIndex === activeLevelIndex;
                const opacity = isActive ? 1 : inactiveLevelOpacity;

                if (opacity === 0) return null;

                const airHandlerObj = objects.find(
                  (obj) =>
                    obj.type === 'air-handler' &&
                    obj.itemId === lineset.airHandlerUniqueId
                );
                const linesetColor = resolveLinesetColor(
                  lineset,
                  displayConfiguration,
                  colorLinesetsByCondenser
                );

                if (configurationId && displayConfiguration) {
                  return (
                    <EditableLineSet3D
                      key={lineset.uniqueId}
                      lineset={lineset}
                      configuration={displayConfiguration}
                      configurationId={configurationId}
                      structure={structure}
                      opacity={opacity}
                      color={linesetColor}
                      airHandlerTypeCode={airHandlerObj?.airHandlerTypeCode}
                      spaces={spaces}
                      offsetX={offsetX}
                      offsetZ={offsetZ}
                      selectedPointUniqueId={
                        selectedPoint?.linesetUniqueId === lineset.uniqueId
                          ? selectedPoint.pointUniqueId
                          : null
                      }
                      onSelectPoint={(pointUniqueId) => {
                        setSelectedPoint(
                          pointUniqueId
                            ? {
                                linesetUniqueId: lineset.uniqueId,
                                pointUniqueId
                              }
                            : null
                        );
                        if (pointUniqueId) {
                          setSelectedEquipmentId(null);
                          setSelectedDuctPointKey(null);
                        }
                      }}
                      readOnly={readOnly}
                    />
                  );
                }

                return (
                  <LineSet3D
                    key={lineset.uniqueId}
                    lineset={lineset}
                    configuration={displayConfiguration}
                    structure={structure}
                    yOffset={yOffset}
                    opacity={opacity}
                    color={linesetColor}
                    airHandlerTypeCode={airHandlerObj?.airHandlerTypeCode}
                    spaces={spaces}
                  />
                );
              })}
              {configurationId &&
                displayConcealedDuctLayouts.map((layout) => {
                  if (!layout.zoneUniqueId) return null;
                  const yOffset = getYOffsetForLevel(layout.originLevelIndex);
                  const placementLevelIndex =
                    getConcealedDuctPlacementLevelIndex(
                      layout.originLevelIndex,
                      layout.placement
                    );
                  const isActive =
                    layout.originLevelIndex === activeLevelIndex ||
                    placementLevelIndex === activeLevelIndex;
                  const opacity = isActive ? 1 : inactiveLevelOpacity;

                  if (opacity === 0) return null;

                  return (
                    <EditableDuctRuns3D
                      key={`duct-runs-${layout.zoneUniqueId}`}
                      layout={layout}
                      structure={structure}
                      configurationId={configurationId}
                      yOffset={yOffset}
                      opacity={opacity}
                      offsetX={offsetX}
                      offsetZ={offsetZ}
                      selectedPointKey={selectedDuctPointKey}
                      onSelectPoint={handleSelectDuctPoint}
                      readOnly={readOnly}
                    />
                  );
                })}
            </group>
            <OrbitControls
              makeDefault
              enablePan
              enableZoom
              enableRotate
              minDistance={8}
              maxDistance={60}
              maxPolarAngle={Math.PI / 2.1}
              minPolarAngle={Math.PI / 12}
              target={[0, targetY, 0]}
            />
          </Suspense>
        </ContextBridge>
      </Canvas>
    </div>
  );
}
